import { session_id } from '../../utils/session_id';
import { createArtifactDashboardApi, DashboardReadApi } from './api';

const BASE_URL = '/API/v2/custom-dashboards';
const REQUEST_TIMEOUT_MS = 120_000;

export const SHARE_TOKEN_KEY = 'share_token';

class ShareRequestError extends Error {
  readonly status: number;
  readonly detail?: string;

  constructor(status: number, detail?: string) {
    super(
      'Client error: ' + (detail || `Request failed with status ${status}`),
    );
    this.name = 'ShareRequestError';
    this.status = status;
    this.detail = detail || undefined;
  }
}

async function errorDetail(response: Response): Promise<string | undefined> {
  try {
    const data = await response.json();
    if (typeof data?.detail === 'string') return data.detail;
    if (Array.isArray(data?.detail)) {
      return data.detail
        .map((entry: { msg?: string }) => entry?.msg)
        .filter(Boolean)
        .join('; ');
    }
  } catch {
    // body was not JSON
  }
  return undefined;
}

// Read-only dashboard API for a shared link: the same viewer code path as
// /custom-dashboards, authenticated with the share token from the login page
// (`Share-Authorization`) instead of the user session. The backend binds the
// token to one dashboard, so the base path pins it. `onUnauthorized` runs when
// the token is missing, expired or revoked; the caller sends the viewer back
// to the share login page.
export function createShareDashboardApi(
  dashboardId: string,
  onUnauthorized: () => void,
): DashboardReadApi {
  const base = `${BASE_URL}/${dashboardId}`;

  return createArtifactDashboardApi(async (method, path, body) => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Request-ID': session_id,
    };
    const token = localStorage.getItem(SHARE_TOKEN_KEY);
    if (token !== null) headers['Share-Authorization'] = `Bearer ${token}`;

    const response = await fetch(`${base}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });

    if (response.status === 401 || response.status === 403) {
      localStorage.removeItem(SHARE_TOKEN_KEY);
      onUnauthorized();
    }

    if (!response.ok) {
      throw new ShareRequestError(response.status, await errorDetail(response));
    }

    return response.json();
  });
}
